import { Bot, Building2, Gauge, Settings, Target } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/cn';
import type { ComponentType } from 'react';

interface BottomNavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  matchPrefix?: string;
}

const NAV_ITEMS: BottomNavItem[] = [
  { to: '/app/dashboard',  label: 'Dashboard',   icon: Gauge },
  { to: '/app/clients',    label: 'Clients',     icon: Building2 },
  { to: '/app/campaigns',  label: 'Campaigns',   icon: Target },
  { to: '/app/automation', label: 'Automations', icon: Bot },
  { to: '/app/settings',   label: 'Settings',    icon: Settings },
];

function BottomNavButton({ item }: { item: BottomNavItem }) {
  const { pathname } = useLocation();
  const isActive = pathname === item.to || pathname.startsWith((item.matchPrefix ?? item.to) + '/');
  const Icon = item.icon;

  return (
    <NavLink
      to={item.to}
      className="flex flex-1 flex-col items-center justify-center gap-[3px] py-2 min-w-0"
      aria-label={item.label}
    >
      <div
        className={cn(
          'relative flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200',
          isActive
            ? 'bg-accent-500/15 shadow-[0_0_12px_rgba(99,102,241,0.25)]'
            : 'bg-transparent'
        )}
      >
        {isActive && (
          <span className="absolute inset-0 rounded-xl ring-1 ring-inset ring-accent-500/25" />
        )}
        <Icon
          className={cn(
            'h-[18px] w-[18px] transition-colors duration-200',
            isActive ? 'text-accent-400' : 'text-white/35'
          )}
        />
      </div>
      <span
        className={cn(
          'truncate text-[10px] font-medium leading-none transition-colors duration-200',
          isActive ? 'text-accent-400' : 'text-white/30'
        )}
      >
        {item.label}
      </span>
    </NavLink>
  );
}

/**
 * Fixed bottom navigation bar — visible only on mobile (below lg breakpoint).
 * Respects iOS/Android safe area insets.
 */
export function MobileBottomNav() {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 lg:hidden"
      aria-label="Mobile navigation"
    >
      {/* Border + blur background */}
      <div
        className="border-t border-white/[0.07] bg-[#0b0b12]/85 backdrop-blur-xl"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex h-16 items-stretch justify-around px-1">
          {NAV_ITEMS.map((item) => (
            <BottomNavButton key={item.to} item={item} />
          ))}
        </div>
      </div>
    </nav>
  );
}
